/**
 * Campaign page → the partnership page as its own block, right above the
 * placements (Doreen, 2026-10-22: it's becoming a main way of working, so
 * it left the row of header buttons). Shows where the page stands — draft
 * or published, sign-ups open, how many brands booked, slots taken, total —
 * with the buttons to open the setup or the page brands see.
 */
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useAssets } from '../../lib/api/assets';
import { usePartnership, usePartnershipPlacements, usePartnershipResponses } from '../../lib/api/partnerships';

const eur = (n: number) => `€${n.toLocaleString('en', { maximumFractionDigits: 0 })}`;

export default function PartnershipStrip({ campaignId }: { campaignId: string }) {
  const navigate = useNavigate();
  const { data: p, isLoading } = usePartnership(campaignId);
  const exists = !!p;
  const { data: rows = [] } = usePartnershipResponses(exists ? campaignId : undefined);
  const { data: placements = [] } = usePartnershipPlacements(exists && !p?.uses_packs ? campaignId : undefined);
  const { data: assets = [] } = useAssets({ campaign: campaignId });
  const withSlots = assets.filter((a) => (a.brand_spots ?? 0) > 0).length;
  if (isLoading) return null;

  const open = () => navigate(`/campaigns/${campaignId}/partnership`);
  const box: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '14px 18px',
    borderRadius: 'var(--r-lg)', marginBottom: 18,
  };

  if (!exists) {
    return (
      <div className="card" style={{ ...box, border: '1px dashed var(--border)', background: 'var(--bg-subtle)' }}>
        <Icon name="globe" size={16} />
        <div style={{ flex: '1 1 260px', fontSize: 13, color: 'var(--ink-3)' }}>
          <strong style={{ color: 'var(--ink)' }}>Sell placements to brands</strong>
          {' — '}
          {withSlots
            ? `${withSlots} placement${withSlots === 1 ? ' has' : 's have'} brand slots. Create the partnership page so brands can book them.`
            : 'Give placements brand slots, then create a partnership page where brands book them.'}
        </div>
        <Button variant="primary" onClick={open}><Icon name="plus" size={13} /> Create partnership page</Button>
      </div>
    );
  }

  const signed = rows.filter((r) => r.status === 'confirmed' || r.status === 'auto-included').length;
  const contacted = rows.filter((r) => r.status !== 'pending').length;
  const spots = placements.reduce((n, r) => n + r.spots_total, 0);
  const taken = placements.reduce((n, r) => n + r.spots_taken, 0);
  const total = rows.filter((r) => r.status === 'confirmed').reduce((n, r) => n + (Number(r.effective_price) || 0), 0);
  const stat = (v: string, l: string) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.1 }}>{v}</div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 2 }}>{l}</div>
    </div>
  );

  return (
    <div className="card" style={{ ...box, borderLeft: '3px solid var(--seasonal)' }}>
      <Icon name="globe" size={16} />
      <div style={{ display: 'grid', gap: 4, minWidth: 180 }}>
        <strong style={{ fontSize: 14 }}>Partnership page</strong>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span className="chip" style={{ background: p.published ? 'var(--seasonal-bg)' : 'var(--bg-subtle)', color: p.published ? 'var(--seasonal)' : 'var(--ink-4)' }}>
            {p.published ? 'Published' : 'Draft'}
          </span>
          <span className="chip" style={{ background: p.closed_for_opt_in ? 'var(--warn-bg)' : 'var(--bg-subtle)', color: p.closed_for_opt_in ? 'var(--warn)' : 'var(--ink-3)' }}>
            {p.closed_for_opt_in ? 'Sign-ups closed' : 'Sign-ups open'}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 28, flex: '1 1 320px', flexWrap: 'wrap' }}>
        {stat(`${signed}`, `signed up${contacted ? ` · ${contacted} contacted` : ''}`)}
        {!p.uses_packs && stat(spots ? `${taken} / ${spots}` : '—', 'slots booked')}
        {stat(total ? eur(total) : '—', 'booked so far')}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {p.published && (
          <Button variant="ghost" onClick={() => window.open(`/partnerships/${p.slug}`, '_blank')} title="The page brands see (general link)">
            View page ↗
          </Button>
        )}
        <Button variant="primary" onClick={open} style={{ background: 'var(--seasonal)', borderColor: 'var(--seasonal)' }}>
          Open setup
        </Button>
      </div>
    </div>
  );
}
